// src/components/Inventory/Usage/AddMovement.js

import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { UserContext } from "../../../UserContext";
import toast from "react-hot-toast";
import {
  FaArrowLeft,
  FaPlus,
  FaLeaf,
  FaExchangeAlt,
  FaSearch,
  FaTimes,
  FaCalendarAlt,
  FaBox,
  FaEllipsisH,
} from "react-icons/fa";
import "./AddMovement.css";

// Movement types matching Android implementation - 4 types
const MOVEMENT_TYPES = {
  LOAD: {
    value: 1,
    label: "Load",
    labelAr: "إضافة",
    apiValue: "load",
    icon: <FaPlus />,
  },
  USAGE: {
    value: 2,
    label: "Usage",
    labelAr: "استخدام",
    apiValue: "usage",
    icon: <FaLeaf />,
  },
  ALLOCATE_PRODUCT: {
    value: 3,
    label: "Allocate Product",
    labelAr: "نقل المنتج",
    apiValue: "allocate",
    icon: <FaExchangeAlt />,
  },
  OTHER: {
    value: 4,
    label: "Other",
    labelAr: "أخرى",
    apiValue: "other",
    icon: <FaEllipsisH />,
  },
};

const AddMovement = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { token, userData } = useContext(UserContext);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [selectedMovementType, setSelectedMovementType] = useState(null);
  const [increaseProduct, setIncreaseProduct] = useState(true); // For OTHER type radio buttons

  const [formData, setFormData] = useState({
    movementType: "",
    quantity: "",
    price: "",
    date: new Date().toISOString().split("T")[0],
    sourceLocationId: "",
    destinationLocationId: "",
    fieldId: "",
    supplierId: "",
    clientId: "",
    notes: "",
  });

  // State for dropdown options
  const [warehouses, setWarehouses] = useState([]);
  const [fields, setFields] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients] = useState([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [loading, setLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});

  // Get farm type from user data
  const farmType =
    userData?.Employee?.Farm?.type || userData?.Farm?.type || "soil";

  // Fetch initial data with retry mechanism for new warehouses
  useEffect(() => {
    const fetchInitialData = async (retryCount = 0) => {
      if (!token || !userData.Employee?.farmId) return;
      const farmId = userData.Employee.farmId;

      // Only show loading on first attempt
      if (retryCount === 0) {
        setLoading(true);
      }

      try {
        const [
          warehousesRes,
          fieldsRes,
          productsRes,
          suppliersRes,
          clientsRes,
        ] = await Promise.all([
          axios.get(`api/inventoryLocation/findByFarmId/${farmId}`, {
            headers: { Authorization: token },
          }),
          axios.get(`api/field/findByFarmId/${farmId}`, {
            headers: { Authorization: token },
          }),
          axios.get(`api/inventoryItemDetails/findByFarmId/${farmId}`, {
            headers: { Authorization: token },
          }),
          axios
            .get(`api/supplier/findByFarmId/${farmId}`, {
              headers: { Authorization: token },
            })
            .catch(() => ({ data: { items: [] } })), // Graceful fallback if endpoint doesn't exist
          axios
            .get(`api/client/findByFarmId/${farmId}`, {
              headers: { Authorization: token },
            })
            .catch(() => ({ data: { items: [] } })), // Graceful fallback if endpoint doesn't exist
        ]);

        const warehousesList = warehousesRes.data.items || [];
        const fieldsList = fieldsRes.data.items || [];
        const productsList = productsRes.data.items || [];
        const suppliersList = suppliersRes.data.items || [];
        const clientsList = clientsRes.data.items || [];

        // Retry logic for eventually consistent data
        // If we recently created a warehouse/product, the API might need a moment to propagate
        if (retryCount < 3) {
          // Check if we're missing expected data
          const shouldRetry =
            (warehousesList.length === 0 && retryCount === 0) || // No warehouses on first try
            (productsList.length === 0 && retryCount === 0); // No products on first try

          if (shouldRetry) {
            const delay = Math.min(1000 * Math.pow(2, retryCount), 3000); // Exponential backoff: 1s, 2s, 3s
            console.log(
              `Retrying data fetch in ${delay}ms (attempt ${retryCount + 1}/3)`
            );
            setTimeout(() => fetchInitialData(retryCount + 1), delay);
            return;
          }
        }

        setWarehouses(warehousesList);
        setFilteredWarehouses(warehousesList); // Initialize filtered warehouses
        setFields(fieldsList);
        setAllProducts(productsList);
        setSuppliers(suppliersList);
        setClients(clientsList);

        // Set default source warehouse if there's only one or if there's a main warehouse
        if (warehousesList.length === 1) {
          setFormData((prev) => ({
            ...prev,
            sourceLocationId: warehousesList[0].id,
          }));
        } else {
          const mainWarehouse = warehousesList.find((w) => w.isMainLocation);
          if (mainWarehouse) {
            setFormData((prev) => ({
              ...prev,
              sourceLocationId: mainWarehouse.id,
            }));
          }
        }
      } catch (error) {
        console.error("Failed to fetch initial data:", error);

        // Retry on network errors
        if (
          retryCount < 2 &&
          (error.code === "ECONNABORTED" || !error.response)
        ) {
          const delay = 1000 * (retryCount + 1);
          console.log(`Network error, retrying in ${delay}ms...`);
          setTimeout(() => fetchInitialData(retryCount + 1), delay);
          return;
        }

        toast.error(t("Failed to load data."));
      } finally {
        if (retryCount === 0 || retryCount >= 2) {
          setLoading(false);
        }
      }
    };

    fetchInitialData();

    // Listen for warehouse creation events
    const handleWarehouseCreated = () => {
      // Delay fetch to allow backend propagation
      setTimeout(() => {
        fetchInitialData();
      }, 1000);
    };

    window.addEventListener("warehouseCreated", handleWarehouseCreated);
    return () =>
      window.removeEventListener("warehouseCreated", handleWarehouseCreated);
  }, [token, userData, t]);

  // Filter warehouses based on product selection - using same API as Android app
  useEffect(() => {
    const filterWarehousesByProduct = async () => {
      // If no product selected, show all warehouses
      if (!selectedProduct || !token) {
        setFilteredWarehouses(warehouses);
        return;
      }

      setLoadingWarehouses(true);
      
      try {
        // Use the same endpoint as Android app: inventoryLocation/findByFarmId/{farmId}?itemId={itemId}
        const response = await axios.get(
          `api/inventoryLocation/findByFarmId/${userData.Employee.farmId}`,
          {
            params: { itemId: selectedProduct.id },
            headers: { Authorization: token }
          }
        );
        
        const productWarehouses = response.data.items || [];
        
        if (productWarehouses.length > 0) {
          // For each warehouse that has the product, fetch the actual quantity
          const warehousesWithQuantities = await Promise.all(
            productWarehouses.map(async (warehouse) => {
              try {
                // Fetch items in this location to get quantities
                const itemsResponse = await axios.get(
                  `api/inventoryItemsAllocation/location/${warehouse.id}`,
                  { headers: { Authorization: token } }
                );
                
                const items = itemsResponse.data.items || [];
                // Find the current product in the items list
                const currentItem = items.find(item => item.id === selectedProduct.id);
                const quantity = currentItem ? (currentItem.currentBalance || 0) : 0;
                
                return {
                  ...warehouse,
                  quantity: Math.max(0, quantity)
                };
              } catch (error) {
                console.log(`Could not fetch quantity for warehouse ${warehouse.id}`);
                return {
                  ...warehouse,
                  quantity: 0
                };
              }
            })
          );
          
          setFilteredWarehouses(warehousesWithQuantities);
        } else {
          // No warehouses have this product yet, show all warehouses for adding
          setFilteredWarehouses(warehouses.map(w => ({ ...w, quantity: 0 })));
        }
      } catch (error) {
        console.log("Could not fetch product-specific warehouses, showing all");
        // Fallback to showing all warehouses
        setFilteredWarehouses(warehouses.map(w => ({ ...w, quantity: 0 })));
      } finally {
        setLoadingWarehouses(false);
      }
    };

    filterWarehousesByProduct();
  }, [selectedProduct, warehouses, token, userData]);

  // Handle product selection
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setProductSearchOpen(false);
    setProductSearchQuery("");
  };

  // Handle movement type change - matching Android logic
  const handleMovementTypeChange = (movementType) => {
    setSelectedMovementType(movementType);
    setFormData((prev) => ({
      ...prev,
      movementType: movementType.value,
      // Reset conditional fields
      fieldId: "",
      destinationLocationId: "",
      supplierId: "",
      clientId: "",
      notes:
        movementType.value !== MOVEMENT_TYPES.OTHER.value ? "" : prev.notes,
      price: "",
    }));

    // Reset increase/decrease for OTHER type
    if (movementType.value === MOVEMENT_TYPES.OTHER.value) {
      setIncreaseProduct(true); // Default to increase
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Real-time quantity validation for stock removal
    if (
      name === "quantity" &&
      selectedMovementType &&
      formData.sourceLocationId
    ) {
      const quantity = parseFloat(value);
      const isRemovingStock =
        selectedMovementType.value === MOVEMENT_TYPES.USAGE.value ||
        selectedMovementType.value === MOVEMENT_TYPES.ALLOCATE_PRODUCT.value ||
        (selectedMovementType.value === MOVEMENT_TYPES.OTHER.value &&
          !increaseProduct);

      if (isRemovingStock && quantity > 0) {
        const selectedWarehouse = filteredWarehouses.find(
          (w) =>
            w.id === parseInt(formData.sourceLocationId) ||
            w.id === formData.sourceLocationId
        );

        if (selectedWarehouse) {
          const availableQuantity = selectedWarehouse.quantity || 0;
          if (quantity > availableQuantity) {
            setValidationErrors((prev) => ({
              ...prev,
              quantity: t("Insufficient stock. Available: {{availableQuantity}} {{unit}}", {
                availableQuantity: availableQuantity,
                unit: selectedProduct?.unit || ""
              }),
            }));
          }
        }
      }
    }
  };

  // Validate form based on movement type
  const validateForm = () => {
    const errors = {};
    const quantityText = formData.quantity.toString().trim();
    const quantity = parseFloat(quantityText);

    if (!selectedProduct) {
      errors.product = t("Product is required");
    }

    if (!selectedMovementType) {
      errors.movementType = t("Movement type is required");
    }

    if (!quantityText || quantity <= 0) {
      errors.quantity = t("Valid quantity is required");
    }

    if (!formData.date) {
      errors.date = t("Date is required");
    }

    if (!formData.sourceLocationId) {
      errors.sourceLocationId = t("Source location is required");
    }

    // Check for sufficient balance when removing stock
    if (selectedMovementType && formData.sourceLocationId && quantity > 0) {
      const isRemovingStock =
        selectedMovementType.value === MOVEMENT_TYPES.USAGE.value ||
        selectedMovementType.value === MOVEMENT_TYPES.ALLOCATE_PRODUCT.value ||
        (selectedMovementType.value === MOVEMENT_TYPES.OTHER.value &&
          !increaseProduct);

      if (isRemovingStock) {
        // Find the selected warehouse and check its quantity
        const selectedWarehouse = filteredWarehouses.find(
          (w) =>
            w.id === parseInt(formData.sourceLocationId) ||
            w.id === formData.sourceLocationId
        );

        if (selectedWarehouse) {
          const availableQuantity = selectedWarehouse.quantity || 0;
          if (quantity > availableQuantity) {
            errors.quantity = t("Insufficient stock. Available: {{availableQuantity}} {{unit}}", {
              availableQuantity: availableQuantity,
              unit: selectedProduct?.unit || ""
            });
          }
        } else {
          // If warehouse not found in filtered list, it might not have the product
          errors.quantity = t(
            "Selected warehouse does not have this product in stock"
          );
        }
      }
    }

    // Movement type specific validations
    if (selectedMovementType?.value === MOVEMENT_TYPES.ALLOCATE_PRODUCT.value) {
      if (!formData.destinationLocationId) {
        errors.destinationLocationId = t("Destination location is required");
      }
      if (formData.sourceLocationId === formData.destinationLocationId) {
        errors.destinationLocationId = t(
          "Source and destination must be different"
        );
      }
    }

    // Field required for USAGE in soil farms only
    if (
      selectedMovementType?.value === MOVEMENT_TYPES.USAGE.value &&
      farmType === "soil" &&
      !formData.fieldId
    ) {
      errors.fieldId = t("Field is required for usage");
    }

    // Notes required for OTHER type
    if (
      selectedMovementType?.value === MOVEMENT_TYPES.OTHER.value &&
      !formData.notes
    ) {
      errors.notes = t("Notes are required for other movement type");
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Helper function to combine selected date with current time
  const getTimestampWithCurrentTime = (dateString) => {
    const [year, month, day] = dateString.split("-");
    const now = new Date();
    now.setFullYear(parseInt(year));
    now.setMonth(parseInt(month) - 1); // Month is 0-indexed
    now.setDate(parseInt(day));
    return now.getTime();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t("Please fill all required fields"));
      return;
    }

    const {
      movementType,
      quantity,
      date,
      notes,
      price,
      sourceLocationId,
      destinationLocationId,
      fieldId,
    } = formData;

    const basePayload = {
      tenantId: userData.Tenant.id,
      farmId: userData.Employee.farmId,
      inventoryItemDetailId: parseInt(selectedProduct.id),
      quantity: parseFloat(quantity),
      movementNotes: notes || "",
    };

    let request;

    // Handle different movement types based on Android logic
    if (movementType === MOVEMENT_TYPES.LOAD.value) {
      // LOAD - use procurement endpoint
      request = axios.post(
        "api/inventoryProcurement",
        {
          ...basePayload,
          locationId: parseInt(sourceLocationId),
          procurementDate: getTimestampWithCurrentTime(date),
          movementType: "load",
          supplierId: formData.supplierId
            ? parseInt(formData.supplierId)
            : null,
          price: price ? parseFloat(price) : null,
        },
        { headers: { Authorization: token } }
      );
    } else if (movementType === MOVEMENT_TYPES.USAGE.value) {
      // USAGE - use distribution endpoint
      request = axios.post(
        "api/inventoryDistribution",
        {
          ...basePayload,
          locationId: parseInt(sourceLocationId),
          distributionDate: getTimestampWithCurrentTime(date),
          movementType: "usage",
          clientId: formData.clientId ? parseInt(formData.clientId) : null,
          fieldId: fieldId ? parseInt(fieldId) : null,
        },
        { headers: { Authorization: token } }
      );
    } else if (movementType === MOVEMENT_TYPES.ALLOCATE_PRODUCT.value) {
      // ALLOCATE_PRODUCT - use move endpoint
      request = axios.post(
        "api/inventoryLocation/move",
        {
          sourceLocation: parseInt(sourceLocationId),
          destinationLocation: parseInt(destinationLocationId),
          itemId: parseInt(selectedProduct.id),
          quantity: parseInt(quantity),
        },
        { headers: { Authorization: token } }
      );
    } else if (movementType === MOVEMENT_TYPES.OTHER.value) {
      // OTHER type - determine endpoint based on increase/decrease
      if (increaseProduct) {
        // Increase - use procurement
        request = axios.post(
          "api/inventoryProcurement",
          {
            ...basePayload,
            locationId: parseInt(sourceLocationId),
            procurementDate: getTimestampWithCurrentTime(date),
            movementType: "other",
            supplierId: formData.supplierId
              ? parseInt(formData.supplierId)
              : null,
            price: price ? parseFloat(price) : null,
          },
          { headers: { Authorization: token } }
        );
      } else {
        // Decrease - use distribution
        request = axios.post(
          "api/inventoryDistribution",
          {
            ...basePayload,
            locationId: parseInt(sourceLocationId),
            distributionDate: getTimestampWithCurrentTime(date),
            movementType: "other",
            clientId: formData.clientId ? parseInt(formData.clientId) : null,
            fieldId: null,
          },
          { headers: { Authorization: token } }
        );
      }
    }

    try {
      await toast.promise(request, {
        loading: t("Saving movement..."),
        success: t("Movement saved successfully!"),
        error: (err) => {
          // Same as Android: Display the backend error message directly
          const message =
            err.response?.data?.message ||
            err.response?.data?.error ||
            err.message ||
            t("Failed to save movement.");

          // Add field-specific error hints
          if (err.response?.status === 400) {
            if (message.toLowerCase().includes("quantity")) {
              return t("Invalid quantity. Please check the amount entered.");
            }
            if (
              message.toLowerCase().includes("warehouse") ||
              message.toLowerCase().includes("location")
            ) {
              return t(
                "Invalid warehouse selection. Please verify your selection."
              );
            }
            if (message.toLowerCase().includes("product")) {
              return t("Invalid product. Please select a valid product.");
            }
          }

          return message;
        },
      });

      // Navigate back to usage page with refresh flag and movement added flag
      navigate("/inventory/usage", {
        state: { refresh: true, movementAdded: true },
      });
    } catch (error) {
      console.error("Submission error:", error);
    }
  };

  const getDisplayName = (item) => {
    if (!item) return "";
    return i18n.language === "ar"
      ? item.nameAr || item.arabicName || item.name
      : item.nameEn ||
          item.englishName ||
          item.name ||
          item.nameAr ||
          item.arabicName;
  };

  const getMovementTypeLabel = (type) => {
    if (!type) return "";
    return i18n.language === "ar" ? type.labelAr : type.label;
  };

  // Filter products for search
  const filteredProducts = allProducts.filter((p) => {
    const name = getDisplayName(p).toLowerCase();
    return name.includes(productSearchQuery.toLowerCase());
  });

  // Determine if price field should be visible
  const isPriceFieldVisible = () => {
    return (
      selectedMovementType?.value === MOVEMENT_TYPES.LOAD.value ||
      (selectedMovementType?.value === MOVEMENT_TYPES.OTHER.value &&
        increaseProduct)
    );
  };

  // Determine if notes field should be visible
  const isNotesFieldVisible = () => {
    return selectedMovementType?.value === MOVEMENT_TYPES.OTHER.value;
  };

  if (loading) {
    return (
      <div className="add-movement-container">
        <div className="loading-spinner">{t("Loading...")}</div>
      </div>
    );
  }

  return (
    <div className="add-movement-container">
      <header className="view-header">
        <div className="header-left-content">
          <button
            className="button-icon-only"
            onClick={() => navigate(-1)}
            aria-label={t("Go back")}
          >
            <FaArrowLeft />
          </button>
          <div>
            <h1 className="view-header-title">{t("Add Inventory Movement")}</h1>
            <p className="view-header-subtitle">
              {t("Record stock in/out transactions")}
            </p>
          </div>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="movement-form-card">
        {/* Product Selection Field */}
        <div
          className={`form-group ${validationErrors.product ? "error" : ""}`}
        >
          <label htmlFor="product">
            {t("Product")} <span className="required">*</span>
          </label>
          <div className="product-select-wrapper">
            <input
              type="text"
              id="product"
              value={selectedProduct ? getDisplayName(selectedProduct) : ""}
              onClick={() => setProductSearchOpen(true)}
              readOnly
              placeholder={t("Select Product")}
              className="product-select-input"
            />
            <button
              type="button"
              className="product-search-btn"
              onClick={() => setProductSearchOpen(true)}
            >
              <FaSearch />
            </button>
          </div>
          {validationErrors.product && (
            <span className="error-message">{validationErrors.product}</span>
          )}
        </div>

        {/* Movement Type Selection - 4 types matching Android */}
        <div
          className={`form-group ${
            validationErrors.movementType ? "error" : ""
          }`}
        >
          <label htmlFor="movementType">
            {t("Movement Type")} <span className="required">*</span>
          </label>
          <select
            id="movementType"
            name="movementType"
            value={formData.movementType}
            onChange={(e) => {
              const selected = Object.values(MOVEMENT_TYPES).find(
                (m) => m.value === parseInt(e.target.value)
              );
              if (selected) handleMovementTypeChange(selected);
            }}
            className="movement-type-select"
          >
            <option value="">{t("Select Movement Type")}</option>
            {Object.values(MOVEMENT_TYPES).map((type) => (
              <option key={type.value} value={type.value}>
                {getMovementTypeLabel(type)}
              </option>
            ))}
          </select>
          {validationErrors.movementType && (
            <span className="error-message">
              {validationErrors.movementType}
            </span>
          )}
        </div>

        {/* Other Type Radio Buttons - Increase/Decrease */}
        {selectedMovementType?.value === MOVEMENT_TYPES.OTHER.value && (
          <div className="form-group">
            <label>{t("Movement Direction")}</label>
            <div className="radio-button-group">
              <label className="radio-button-label">
                <input
                  type="radio"
                  name="increaseDecrease"
                  checked={increaseProduct}
                  onChange={() => setIncreaseProduct(true)}
                />
                <span className="radio-button-text">
                  {t("Increased Quantity")}
                </span>
              </label>
              <label className="radio-button-label">
                <input
                  type="radio"
                  name="increaseDecrease"
                  checked={!increaseProduct}
                  onChange={() => setIncreaseProduct(false)}
                />
                <span className="radio-button-text">
                  {t("Decreased Quantity")}
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Source Location - Warehouse selection */}
        <div
          className={`form-group ${
            validationErrors.sourceLocationId ? "error" : ""
          }`}
        >
          <label htmlFor="sourceLocationId">
            {t("Warehouse")} <span className="required">*</span>
          </label>
          <select
            id="sourceLocationId"
            name="sourceLocationId"
            value={formData.sourceLocationId}
            onChange={handleChange}
            disabled={loadingWarehouses}
          >
            <option value="">
              {loadingWarehouses
                ? t("Loading warehouses...")
                : t("Select Warehouse")}
            </option>
            {/* Always show filtered warehouses based on product selection */}
            {filteredWarehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {getDisplayName(w)}
                {w.quantity !== undefined &&
                  selectedProduct &&
                  ` (${w.quantity} ${selectedProduct.unit || ""})`}
              </option>
            ))}
          </select>
          {validationErrors.sourceLocationId && (
            <span className="error-message">
              {validationErrors.sourceLocationId}
            </span>
          )}
        </div>

        {/* Supplier Selection - Only for LOAD movements */}
        {selectedMovementType?.value === MOVEMENT_TYPES.LOAD.value && (
          <div className="form-group">
            <label htmlFor="supplierId">{t("Supplier")}</label>
            <select
              id="supplierId"
              name="supplierId"
              value={formData.supplierId}
              onChange={handleChange}
            >
              <option value="">{t("Select Supplier")}</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {getDisplayName(s)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Client Selection - Only for USAGE movements */}
        {selectedMovementType?.value === MOVEMENT_TYPES.USAGE.value && (
          <div className="form-group">
            <label htmlFor="clientId">{t("Client")}</label>
            <select
              id="clientId"
              name="clientId"
              value={formData.clientId}
              onChange={handleChange}
            >
              <option value="">{t("Select Client")}</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {getDisplayName(c)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Destination Location - Only for ALLOCATE_PRODUCT */}
        {selectedMovementType?.value ===
          MOVEMENT_TYPES.ALLOCATE_PRODUCT.value && (
          <div
            className={`form-group ${
              validationErrors.destinationLocationId ? "error" : ""
            }`}
          >
            <label htmlFor="destinationLocationId">
              {t("Destination")} <span className="required">*</span>
            </label>
            <select
              id="destinationLocationId"
              name="destinationLocationId"
              value={formData.destinationLocationId}
              onChange={handleChange}
            >
              <option value="">{t("Select Destination")}</option>
              {/* Show ALL warehouses for destination, not filtered by product */}
              {warehouses
                .filter((w) => w.id !== parseInt(formData.sourceLocationId))
                .map((w) => (
                  <option key={w.id} value={w.id}>
                    {getDisplayName(w)}
                  </option>
                ))}
            </select>
            {validationErrors.destinationLocationId && (
              <span className="error-message">
                {validationErrors.destinationLocationId}
              </span>
            )}
          </div>
        )}

        {/* Field Selection - Only for USAGE in SOIL farms */}
        {selectedMovementType?.value === MOVEMENT_TYPES.USAGE.value &&
          farmType === "soil" && (
            <div
              className={`form-group ${
                validationErrors.fieldId ? "error" : ""
              }`}
            >
              <label htmlFor="fieldId">
                {t("Field")} <span className="required">*</span>
              </label>
              <select
                id="fieldId"
                name="fieldId"
                value={formData.fieldId}
                onChange={handleChange}
              >
                <option value="">{t("Select Field")}</option>
                {fields.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
              {validationErrors.fieldId && (
                <span className="error-message">
                  {validationErrors.fieldId}
                </span>
              )}
            </div>
          )}

        {/* Quantity Field */}
        <div
          className={`form-group ${validationErrors.quantity ? "error" : ""}`}
        >
          <label htmlFor="quantity">
            {t("Quantity")} <span className="required">*</span>
          </label>
          <input
            type="number"
            step="any"
            id="quantity"
            name="quantity"
            value={formData.quantity}
            onChange={handleChange}
            placeholder={t("0.00")}
          />
          {validationErrors.quantity && (
            <span className="error-message">{validationErrors.quantity}</span>
          )}
        </div>

        {/* Notes Field - Only for OTHER type (required) */}
        {isNotesFieldVisible() && (
          <div
            className={`form-group ${validationErrors.notes ? "error" : ""}`}
          >
            <label htmlFor="notes">
              {t("Notes")} <span className="required">*</span>
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              placeholder={t("Enter notes...")}
            ></textarea>
            {validationErrors.notes && (
              <span className="error-message">{validationErrors.notes}</span>
            )}
          </div>
        )}

        {/* Price Field - For LOAD and OTHER (increase) */}
        {isPriceFieldVisible() && (
          <div className="form-group">
            <label htmlFor="price">{t("Price")}</label>
            <input
              type="number"
              step="any"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              placeholder={t("0.00")}
            />
          </div>
        )}

        {/* Date Field */}
        <div className={`form-group ${validationErrors.date ? "error" : ""}`}>
          <label htmlFor="date">
            {t("Date")} <span className="required">*</span>
          </label>
          <div className="date-input-wrapper">
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              max={new Date().toISOString().split("T")[0]}
            />
            <FaCalendarAlt className="date-icon" />
          </div>
          {validationErrors.date && (
            <span className="error-message">{validationErrors.date}</span>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="button-secondary"
            onClick={() => navigate(-1)}
          >
            {t("Cancel")}
          </button>
          <button type="submit" className="button-primary">
            {t("Save")}
          </button>
        </div>
      </form>

      {/* Product Search Modal */}
      {productSearchOpen && (
        <div
          className="movement-modal-overlay"
          onClick={() => setProductSearchOpen(false)}
        >
          <div
            className="movement-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="movement-modal-header">
              <h2>{t("Select Product")}</h2>
              <button
                className="movement-modal-close"
                onClick={() => setProductSearchOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="movement-modal-search">
              <input
                type="text"
                placeholder={t("Search products...")}
                value={productSearchQuery}
                onChange={(e) => setProductSearchQuery(e.target.value)}
                autoFocus
              />
            </div>

            <div className="movement-products-list">
              {filteredProducts.length === 0 ? (
                <div className="movement-no-products">
                  {t("No products found")}
                </div>
              ) : (
                filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="movement-product-item"
                    onClick={() => handleProductSelect(product)}
                  >
                    <div className="movement-product-content">
                      <FaBox className="movement-product-icon" />
                      <div className="movement-product-info">
                        <div className="movement-product-name">
                          {getDisplayName(product)}
                        </div>
                        {product.unit && (
                          <div className="movement-product-unit">
                            {product.unit}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddMovement;
